/**
 * Determines whether `url` is proxied through our own origin — i.e. it resolves
 * to the same origin as `baseUrl` and sits under `pathPrefix`, so it is served
 * from our domain rather than a third party.
 *
 * Relative URLs resolve against the origin of `baseUrl` (any path on `baseUrl`
 * is ignored) and are therefore proxied by definition, provided they match
 * `pathPrefix`. Absolute same-origin URLs are also treated as proxied;
 * cross-origin URLs are not.
 *
 * `pathPrefix` (e.g. '/cvg/' for the Converge proxy) is required so that
 * same-origin URLs outside the proxy mount point are not misclassified — pass
 * '/' to match any same-origin path.
 *
 * Returns `false` for malformed input (including an invalid `baseUrl`).
 *
 * @example
 * isProxiedUrl('/cvg/static/pixels/abc.js', 'https://www.example.com', '/cvg/') // => true
 * isProxiedUrl('https://www.example.com/cvg/abc.js', 'https://www.example.com', '/cvg/') // => true
 * isProxiedUrl('/other/path.js', 'https://www.example.com', '/cvg/') // => false
 *
 * See ./isProxiedUrl.test.ts for more examples
 */
export const isProxiedUrl = (
  url: string,
  baseUrl: string,
  pathPrefix: string,
) => {
  try {
    const { origin } = new URL(baseUrl);
    const resolved = new URL(url, origin);
    return (
      resolved.origin === origin && resolved.pathname.startsWith(pathPrefix)
    );
  } catch {
    return false;
  }
};
